import { Link } from 'found';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Card, CardBody, CardFooter, CardSubtitle, CardText, CardTitle,
} from 'reactstrap';

import UUID from 'src/components/UUID';
import { APIConfig } from 'src/config';
import Time from 'src/enosikit/components/Time/components/Time';
import { TRADE_TYPE_NOMINATED } from 'src/util/constants';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import { getStringAndNumericFormattedDate } from 'src/util/i18n/helpers';
import { getTradeRuleMeterLabel } from 'src/util/i18n/trade';
import { buildTradeRulePriceInformation } from 'src/util/i18n/tradeRule';
import { getPriceRange } from 'src/util/tradeRule';

/**
 * Renders a trade_rule component displaying trade rule information for community trades,
 * contracted trades, and residual trades.
 * Shows trade rule details including meter information, proposal/acceptance timestamps,
 * price range, and navigation links to detailed trade rule view.
 * @param {object} props - Component props
 * @param {string} props.tradeDirection - Trade direction (BUY or SELL)
 * @param {string} props.propertyId - UUID of the property containing the trade rule
 * @param {object} props.rule - TradeRule object from GraphQL (TradeRule type)
 * @param {string} props.rule.uuid - Unique identifier for the trade rule
 * @param {object} props.rule.meter - Meter object associated with the trade rule
 * @param {string} props.rule.meter.title - Display title of the meter
 * @param {number} props.rule.acceptedAt - Unix timestamp when rule was accepted
 * @param {object} props.rule.acceptedBy - User who accepted the rule (User type)
 * @param {string} props.rule.acceptedBy.uuid - UUID of accepting user
 * @param {string} props.rule.acceptedBy.email - Email of accepting user
 * @param {string} props.rule.acceptedBy.givenName - Given name of accepting user
 * @param {string} props.rule.acceptedBy.familyName - Family name of accepting user
 * @param {number} props.rule.proposedAt - Unix timestamp when rule was proposed
 * @param {object} props.rule.proposedBy - User who proposed the rule (User type)
 * @param {string} props.rule.proposedBy.uuid - UUID of proposing user
 * @param {string} props.rule.proposedBy.email - Email of proposing user
 * @param {string} props.rule.proposedBy.givenName - Given name of proposing user
 * @param {string} props.rule.proposedBy.familyName - Family name of proposing user
 * @param {object} props.rule.buyer - Trader object representing the buyer
 * @param {object} props.rule.seller - Trader object representing the seller
 * @param {object} props.rule.clauses - Connection object containing price clauses
 * @param {object} props.rule.priority
 * @param {string} props.tradeType - Trade type (COMMUNITY or CONTRACTED)
 * @param {Function} [props.actionButtonFn] - Function to generate action buttons for the trade rule
 * @returns {React.ReactElement} Card component displaying trade rule information
 */
function TradeRule(props) {
  const {
    tradeDirection, propertyId, rule, tradeType, actionButtonFn,
  } = props || {};
  const {
    acceptedAt, acceptedBy, uuid: ruleId, meter, proposedAt, proposedBy, buyer, seller, priority,
  } = rule || {};
  const { title: tradePointTitle } = meter || {};
  const priceRange = getPriceRange(rule);
  const { maximum, minimum } = priceRange || {};
  const intl = useIntl();

  const approvedTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.trade_rule.buy.approved.title', defaultMessage: 'Approved' });
  const furtherDetailsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.trade_rule.buy.further_details', defaultMessage: 'further details' });
  const tradePointPriorityTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.trade_rule.nominative.buy.priority.label', defaultMessage: 'Priority {priority} for {tradePointTitle}' }, { priority, tradePointTitle });
  const tradeRuleIdLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.trade_rule.id.label', defaultMessage: 'Trade Rule ID:' });
  const copyTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.trade_rule.copy_title', defaultMessage: 'Copy trade rule id to the clipboard' });
  const copySuccessMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_active.trade_rule.copy.success.message', defaultMessage: 'Trade rule id copied to the clipboard.' });
  const cardTitle = tradeType === TRADE_TYPE_NOMINATED ? tradePointPriorityTitle : tradePointTitle;

  return (
    <Card className="mt-4 mb-4" key={`trade-rules-${ruleId}`}>
      <CardBody>
        <CardTitle tag="h4">
          {cardTitle}
        </CardTitle>
        <CardSubtitle tag="h5" className="mb-2">
          {approvedTitle}
        </CardSubtitle>
        <CardText>
          {getTradeRuleMeterLabel(intl, buyer, seller, tradeDirection, tradeType)}
          <br />
          <FormattedMessage
            id="trade_rule.trade_rule_active.trade_rule.buy.rule.proposed"
            defaultMessage="Proposed by {actor} on {timestamp}"
            values={{
              actor: <strong>{displayUserName(intl, proposedBy)}</strong>,
              timestamp: <Time
                child={getStringAndNumericFormattedDate(intl, proposedAt)}
                unixTimestamp={proposedAt}
              />,
            }}
          />
          <br />
          {acceptedAt && (
          <FormattedMessage
            id="trade_rule.trade_rule_active.trade_rule.buy.rule.accepted"
            defaultMessage="Accepted by {actor} on {timestamp}"
            values={{
              actor: <strong>{displayUserName(intl, acceptedBy)}</strong>,
              timestamp: <Time
                child={getStringAndNumericFormattedDate(intl, acceptedAt)}
                unixTimestamp={acceptedAt}
              />,
            }}
          />
          )}
          <br />
          {buildTradeRulePriceInformation({
            detailsLink: <Link to={`/properties/${propertyId}/trade-rules/${ruleId}`}>{furtherDetailsLabel}</Link>,
            direction: tradeDirection,
            intl,
            maximum,
            minimum,
            mode: APIConfig().MODE,
          })}
          <br />
          <>
            <span className="me-1">{tradeRuleIdLabel}</span>
            <UUID
              copySuccessMsg={copySuccessMsg}
              copyTitle={copyTitle}
              uuid={ruleId}
              url={`/properties/${propertyId}/trade-rules/${ruleId}`}
            />
          </>
        </CardText>
      </CardBody>
      {(tradeType === TRADE_TYPE_NOMINATED) && (
        <CardFooter>{actionButtonFn(intl, rule)}</CardFooter>
      )}
    </Card>
  );
}

export default TradeRule;
